import { useMemo, useState } from "react";
import PageHeader from "@/components/base/PageHeader";
import Button from "@/components/base/Button";
import StatCard from "@/components/base/StatCard";
import { Input, Select } from "@/components/base/Field";
import { LoadingState, ErrorState, EmptyState } from "@/components/base/StateViews";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import { useToast } from "@/components/base/Toast";
import { useAsync } from "@/hooks/useAsync";
import { usePager } from "@/hooks/usePager";
import Pagination from "@/components/base/Pagination";
import { useAuth } from "@/contexts/AuthContext";
import {
  getPlatformStats,
  listSchools,
  updateSchool,
  type CreatedAdmin,
} from "@/services/platformService";
import SchoolCard from "./components/SchoolCard";
import SchoolFormModal from "./components/SchoolFormModal";
import PlatformComparison from "./components/PlatformComparison";
import AccountRequestsPanel from "./components/AccountRequestsPanel";
import TemporaryPasswordDialog from "@/components/feature/TemporaryPasswordDialog";
import type { School } from "@/types";
import { useUrlSearch } from "@/hooks/useUrlSearch";
import { plural } from "@/utils/format";

export default function PlatformPage() {
  const { notify } = useToast();
  const { refreshSchools, can } = useAuth();
  const { data, loading, error, reload } = useAsync(listSchools, []);
  const statsState = useAsync(getPlatformStats, []);

  const [search, setSearch] = useUrlSearch();
  const [status, setStatus] = useState("toutes");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<School | null>(null);
  /** École à désactiver ou réactiver (confirmation). */
  const [toToggle, setToToggle] = useState<School | null>(null);
  const [created, setCreated] = useState<{ admin: CreatedAdmin; schoolName: string } | null>(null);
  const pager = usePager();

  const schools = useMemo(() => data ?? [], [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return schools.filter((s) => {
      const matchQuery =
        !q ||
        s.name.toLowerCase().includes(q) ||
        s.shortName.toLowerCase().includes(q) ||
        s.code.toLowerCase().includes(q) ||
        s.city.toLowerCase().includes(q);
      const matchStatus =
        status === "toutes" || (status === "actives" ? s.isActive : !s.isActive);
      return matchQuery && matchStatus;
    });
  }, [schools, search, status]);
  const page = pager.slice(filtered, `${search}|${status}`);

  const stats = useMemo(() => {
    const active = schools.filter((s) => s.isActive).length;
    const students = schools.reduce((sum, s) => sum + s.studentsCount, 0);
    const staff = schools.reduce((sum, s) => sum + s.staffCount, 0);
    return { total: schools.length, active, students, staff };
  }, [schools]);

  const refreshAll = async () => {
    await Promise.all([reload(), statsState.reload(), refreshSchools()]);
  };

  const handleSaved = (school: School, admin: CreatedAdmin | null) => {
    if (admin) setCreated({ admin, schoolName: school.name });
    void refreshAll();
  };

  const handleToggle = async () => {
    if (!toToggle) return;
    try {
      await updateSchool(toToggle.id, { isActive: !toToggle.isActive });
      notify(
        toToggle.isActive
          ? `École « ${toToggle.shortName} » désactivée : ses comptes ne peuvent plus se connecter.`
          : `École « ${toToggle.shortName} » réactivée.`,
      );
      await refreshAll();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Opération impossible.", "error");
    } finally {
      setToToggle(null);
    }
  };

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Plateforme & Écoles"
        subtitle="Super-administration : ouvrez, configurez et suivez chaque établissement (totaux uniquement)"
        icon="ri-building-2-line"
        actions={
          can("platform:create") ? (
            <Button
              icon="ri-add-line"
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
            >
              Ouvrir une école
            </Button>
          ) : null
        }
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Écoles" value={String(stats.total)} icon="ri-building-2-line" tone="primary" hint={`${plural(stats.active, "active")}`} />
        <StatCard label="Écoles actives" value={String(stats.active)} icon="ri-checkbox-circle-line" tone="accent" />
        <StatCard label="Élèves (total)" value={stats.students.toLocaleString("fr-FR")} icon="ri-user-3-line" tone="secondary" />
        <StatCard label="Personnel (total)" value={stats.staff.toLocaleString("fr-FR")} icon="ri-team-line" tone="primary" />
      </div>

      <AccountRequestsPanel onChanged={refreshAll} />

      <div className="mt-6 mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
          <div className="w-full sm:max-w-xs">
            <Input
              placeholder="Rechercher une école, un code, une ville..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="w-full sm:max-w-[190px]">
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="toutes">Toutes les écoles</option>
              <option value="actives">Actives uniquement</option>
              <option value="inactives">Inactives uniquement</option>
            </Select>
          </div>
        </div>
        <p className="text-xs text-foreground-500 whitespace-nowrap">
          {plural(filtered.length, "école affichée", "écoles affichées")}
        </p>
      </div>

      {loading ? (
        <LoadingState label="Chargement des écoles..." />
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="ri-building-2-line"
          title="Aucune école trouvée"
          message="Ajustez votre recherche ou ajoutez une nouvelle école à la plateforme."
          action={
            can("platform:create") ? (
              <Button
                icon="ri-add-line"
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
              >
                Ouvrir une école
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {page.items.map((school) => (
            <SchoolCard
              key={school.id}
              school={school}
              onConfigure={
                can("platform:update")
                  ? (s) => {
                      setEditing(s);
                      setFormOpen(true);
                    }
                  : undefined
              }
              onToggleActive={can("platform:update") ? setToToggle : undefined}
            />
          ))}
        </div>
        <Pagination pager={page} className="mt-3 rounded-md border border-background-200 bg-background-50" />
        </>
      )}

      <PlatformComparison schools={schools} stats={statsState.data ?? []} />

      <SchoolFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        school={editing}
        onSaved={handleSaved}
      />

      <ConfirmDialog
        open={toToggle !== null}
        title={toToggle?.isActive ? "Désactiver l'école" : "Réactiver l'école"}
        message={
          toToggle?.isActive
            ? `Les comptes de « ${toToggle.name} » ne pourront plus se connecter. Ses données sont conservées et l'école peut être réactivée à tout moment.`
            : `Les comptes de « ${toToggle?.name ?? ""} » pourront de nouveau se connecter.`
        }
        confirmLabel={toToggle?.isActive ? "Désactiver" : "Réactiver"}
        onConfirm={handleToggle}
        onClose={() => setToToggle(null)}
      />

      <TemporaryPasswordDialog
        admin={created?.admin ?? null}
        schoolName={created?.schoolName ?? ""}
        onClose={() => setCreated(null)}
      />
    </div>
  );
}